import { FunctionComponent, useEffect, useState } from 'react'
import styles from './places.module.scss'
import { useQuery } from '@apollo/client'
import { OccasionalSlotsPlaces } from '@/types/Slots'
import { GET_PLACES } from '@/modules/API/daycare/daycareHandler'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import PlacesTableRow from './placesTableRow'
import PlaceDialog from './Dialog/placeDialog'
import Title from '@/components/_custom/Title/title'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useTranslation } from 'react-i18next'
import Loading from '@/components/Loading/Loading'
import Desktop from '@/modules/desktopMobile/desktop'
import { useAuth } from '@/modules/context/AuthContext'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import { useCurrentDaycareInfos } from '@/hooks/useCurrentDaycareInfos'
import { useDaycareRight } from '@/hooks/useDaycareRight'

const Places: FunctionComponent = () => {
    const { t } = useTranslation()

    const { fetchCurrentDaycare } = useAuth()

    const { currentDaycare: daycare } = useCurrentDaycareInfos()

    const canUpdate: boolean = useDaycareRight('canWriteParamsDaycare')

    const [places, setPlaces] = useState<OccasionalSlotsPlaces[]>([])

    const [isDialogCreatePlaceOpen, setDialogCreatePlaceOpen] = useState<boolean>(false)

    const {
        data: dataPlaces,
        loading: loadingPlaces,
        error: errorPlaces
    } = useQuery<{
        getOccasionalSlotsPlaces: OccasionalSlotsPlaces[]
    }>(GET_PLACES, {
        fetchPolicy: 'network-only',
        variables: { daycareId: daycare.id }
    })

    useEffect(() => {
        if (dataPlaces) {
            setPlaces(
                [...dataPlaces.getOccasionalSlotsPlaces].sort((a: OccasionalSlotsPlaces, b: OccasionalSlotsPlaces) =>
                    a.name.localeCompare(b.name)
                )
            )
        } else {
            setPlaces([])
        }
    }, [dataPlaces])

    function addOnePlace(place: OccasionalSlotsPlaces): void {
        const newPlace: OccasionalSlotsPlaces[] = [...places, place].sort(
            (a: OccasionalSlotsPlaces, b: OccasionalSlotsPlaces) => a.name.localeCompare(b.name)
        )
        setPlaces([])
        setPlaces(newPlace)
        fetchCurrentDaycare()
    }

    function removeOnePlace(placeId: number): void {
        const newPlaces: OccasionalSlotsPlaces[] = places.filter((place: OccasionalSlotsPlaces) => place.id !== placeId)
        setPlaces([])
        setPlaces(newPlaces)
        fetchCurrentDaycare()
    }

    function updateOnePlace(place: OccasionalSlotsPlaces): void {
        const placeList: OccasionalSlotsPlaces[] = places.filter((p: OccasionalSlotsPlaces) => p.id !== place.id)
        setPlaces([])
        setPlaces([...placeList, place].sort((a, b) => a.name.localeCompare(b.name)))
        fetchCurrentDaycare()
    }

    return (
        <div className={styles.PlacePlacement}>
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <Title text={t('myPlaces')} className={styles.Title} />
                    {canUpdate && (
                        <ButtonTextIcon
                            type={'add'}
                            className={styles.Button}
                            onClick={() => setDialogCreatePlaceOpen(true)}
                        />
                    )}
                </div>
                {loadingPlaces && <Loading overall={false} />}
                {dataPlaces && (
                    <>
                        <TableContainer className={styles.TablePlacement}>
                            <Table className={styles.Table}>
                                <TableHead className={styles.TableHeader}>
                                    <TableRow>
                                        <TableCell sx={{ width: '35%' }}>{t('name')}</TableCell>
                                        <Desktop>
                                            <TableCell sx={{ width: '55%' }}>{t('address')}</TableCell>
                                        </Desktop>
                                        {canUpdate && (
                                            <TableCell sx={{ width: '10%' }} colSpan={2}>
                                                {t('actions')}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {places.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4}>{t('noPlaces')}</TableCell>
                                        </TableRow>
                                    ) : (
                                        places.map((place: OccasionalSlotsPlaces, index) => (
                                            <PlacesTableRow
                                                key={index + place.name + place.id + place.address}
                                                place={place}
                                                daycareId={daycare.id}
                                                canUpdate={canUpdate}
                                                updateOnePlace={updateOnePlace}
                                                removeOnePlace={removeOnePlace}
                                            />
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <PlaceDialog
                            daycareId={daycare.id}
                            isDialogOpen={isDialogCreatePlaceOpen}
                            handleCloseDialog={() => setDialogCreatePlaceOpen(false)}
                            addOnePlace={addOnePlace}
                            create={true}
                        />
                    </>
                )}
                {errorPlaces && <ErrorMessage error={t('generalError')} />}
            </div>
        </div>
    )
}

export default Places
